import { CodeParameters } from '../models/sales/code-parameters';
import { SalesOrderRequirements } from '../models/sales/sales-order-requirements';

// The Check column of the What's Happened tab's Requirements panel (D-30), its line notes,
// and the codes the Sales Order's Confirm button prompts on (D-32). Mirrors
// TMSWeb.Common.Constants.RequirementsChecks: the API sends a CODE plus parameters (D-9) and the
// words are here in $localize. The two lists must stay in step.
export enum RequirementsChecks {
  // Nothing still to allocate. Parameter: rolls.
  allocated = 'Allocated',
  // Nothing still to allocate, from more than one roll. Parameter: rolls.
  allocatedFromRolls = 'AllocatedFromRolls',
  oneRollCanCover = 'OneRollCanCover',
  // Prompts on Confirm.
  moreThanOneRoll = 'MoreThanOneRoll',
  // Prompts on Confirm. Parameter: quantity.
  short = 'Short',
  // A line note; prompts on Confirm. Parameters: line, quantity.
  noRollLongEnough = 'NoRollLongEnough',
}

// Quantities arrive as invariant strings ("24.86"). A screen passes its own formatter so the user
// reads them in their locale; without one they show as sent.
export type RequirementsQuantityFormatter = (quantity: string) => string;

const asSent: RequirementsQuantityFormatter = (quantity: string) => quantity;

// Only a quantity that was sent is formatted - a check without one has nothing to show.
function formattedQuantity(
  value: CodeParameters | undefined | null,
  formatQuantity: RequirementsQuantityFormatter,
): string {
  const quantity = value?.parameters?.['quantity'];
  return quantity ? formatQuantity(quantity) : '';
}

// The check as a person reads it, e.g. "Short by 24.86 m".
export function requirementsCheckText(
  check: CodeParameters | undefined | null,
  unit: string,
  formatQuantity: RequirementsQuantityFormatter = asSent,
): string {
  const rolls = check?.parameters?.['rolls'] ?? '';
  const quantity = formattedQuantity(check, formatQuantity);
  const uom = unit ?? '';
  switch (check?.code) {
    case RequirementsChecks.allocated:
      return $localize`Allocated`;
    case RequirementsChecks.allocatedFromRolls:
      return $localize`Allocated from ${rolls}:rolls: rolls`;
    case RequirementsChecks.oneRollCanCover:
      return $localize`One roll can cover this`;
    case RequirementsChecks.moreThanOneRoll:
      return $localize`Enough stock, but on more than one roll`;
    case RequirementsChecks.short:
      return $localize`Short by ${quantity}:quantity: ${uom}:unit:`.trim();
    default:
      // Anything unrecognised shows as sent rather than vanishing.
      return check?.code ?? '';
  }
}

// A line note as a person reads it: "Line 2 needs 5.65 m; no free roll is long enough".
export function requirementsNoteText(
  note: CodeParameters | undefined | null,
  unit: string,
  formatQuantity: RequirementsQuantityFormatter = asSent,
): string {
  const line = note?.parameters?.['line'] ?? '';
  const quantity = formattedQuantity(note, formatQuantity);
  const uom = unit ?? '';
  switch (note?.code) {
    case RequirementsChecks.noRollLongEnough:
      return $localize`Line ${line}:line: needs ${quantity}:quantity: ${uom}:unit:; no free roll is long enough`;
    default:
      return note?.code ?? '';
  }
}

// Every note on a row, joined into one cell as the Stock cell is.
export function requirementsNotesText(
  row: SalesOrderRequirements | undefined | null,
  formatQuantity: RequirementsQuantityFormatter = asSent,
): string {
  return (row?.notes ?? []).map((note) => requirementsNoteText(note, row.unit, formatQuantity)).join(', ');
}

// Does this requirement stop Confirm with the "Confirm anyway?" prompt (D-32)? Short, or enough stock
// only across several rolls, or a line no single free roll can cover.
export function requirementsCheckNeedsPrompt(row: SalesOrderRequirements | undefined | null): boolean {
  if (!row) {
    return false;
  }
  return (
    row.check?.code === RequirementsChecks.short ||
    row.check?.code === RequirementsChecks.moreThanOneRoll ||
    (row.notes ?? []).some((note) => note?.code === RequirementsChecks.noRollLongEnough)
  );
}
